import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Car,
  Calendar,
  DollarSign,
  AlertCircle,
  CheckCircle,
  Eye,
  Grid,
  List,
  ChevronLeft,
  ChevronRight,
  Filter,
  X,
  Camera,
  Gauge,
} from 'lucide-react'
import {
  Button,
  Input,
  Card,
  CardContent,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  ConfirmDialog,
  StatsCard,
  ListPageSkeleton,
  Badge,
} from '../../components/ui'
import { VehicleFilters as VehicleFiltersComponent, VehicleFiltersData } from '../../components/filters'
import { useVehicles, Vehicle } from '../../hooks/useVehicles'
import { useCatalog } from '../../hooks/useCatalog'
import { useVehicleOptions } from '../../hooks/useVehicleOptions'
import { VehicleFiltersAPI } from '../../types'
import { cn } from '../../lib/utils'

const STATUS_CONFIG: Record<string, { labelKey: string; variant: 'success' | 'warning' | 'danger' | 'info' }> = {
  in_stock: { labelKey: 'vehicles.status.inStock', variant: 'success' },
  reserved: { labelKey: 'vehicles.status.reserved', variant: 'warning' },
  sold: { labelKey: 'vehicles.status.sold', variant: 'danger' },
  in_transit: { labelKey: 'vehicles.status.inTransit', variant: 'info' },
}

const EMPTY_FILTERS: VehicleFiltersData = {
  search: '',
  status: 'in_stock',
  condition: '',
  category: 'B',
  brandId: '',
  modelId: '',
  versionId: '',
  minYear: '',
  maxYear: '',
  minMileage: '',
  maxMileage: '',
  fuelType: '',
  plateEnd: '',
  armorLevel: '',
  color: '',
  type: '',
  doors: '',
  isAuction: '',
  specialCategory: '',
  characteristics: [],
}

export const VehiclesPage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { vehicles, isLoading, error, pagination, page, fetchVehicles, deleteVehicle } = useVehicles()
  const total = pagination.total
  const { brands, models, versions, fetchBrands, fetchModels, fetchVersions } = useCatalog()
  const { options } = useVehicleOptions()

  const [filters, setFilters] = useState<VehicleFiltersData>(EMPTY_FILTERS)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [vehicleToDelete, setVehicleToDelete] = useState<Vehicle | null>(null)
  const [successMessage, setSuccessMessage] = useState('')
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [photoIndexes, setPhotoIndexes] = useState<Record<string, number>>({})
  const [isInitialMount, setIsInitialMount] = useState(true)
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false)

  useEffect(() => {
    const category = filters.category || 'B'
    fetchBrands(category)
  }, [filters.category])

  useEffect(() => {
    if (filters.brandId) fetchModels(filters.brandId)
  }, [filters.brandId])

  useEffect(() => {
    if (filters.modelId) fetchVersions(filters.modelId)
  }, [filters.modelId])

  useEffect(() => {
    if (isInitialMount) {
      setIsInitialMount(false)
      applyFilters()
    } else {
      const timeoutId = setTimeout(() => applyFilters(), filters.search ? 500 : 0)
      return () => clearTimeout(timeoutId)
    }
  }, [filters, page])

  useEffect(() => {
    if (location.state?.successMessage) {
      setSuccessMessage(location.state.successMessage)
      window.history.replaceState({}, document.title)
      setTimeout(() => setSuccessMessage(''), 5000)
    }
  }, [location])

  const applyFilters = () => {
    const params: VehicleFiltersAPI = { page, limit: 50 }
    if (filters.search) params.search = filters.search
    if (filters.status) params.status = filters.status
    if (filters.condition) params.condition = filters.condition
    if (filters.category) params.category = filters.category
    if (filters.brandId) params.brandId = filters.brandId
    if (filters.modelId) params.modelId = filters.modelId
    if (filters.versionId) params.versionId = filters.versionId
    if (filters.minYear) params.minYear = parseInt(filters.minYear)
    if (filters.maxYear) params.maxYear = parseInt(filters.maxYear)
    if (filters.minMileage) params.minMileage = parseInt(filters.minMileage)
    if (filters.maxMileage) params.maxMileage = parseInt(filters.maxMileage)
    if (filters.fuelType) params.fuelType = filters.fuelType
    if (filters.plateEnd) params.plateEnd = filters.plateEnd
    if (filters.armorLevel) params.armorLevel = filters.armorLevel
    if (filters.color) params.color = filters.color
    if (filters.type) params.type = filters.type
    if (filters.doors) params.doors = parseInt(filters.doors)
    if (filters.isAuction) params.isAuction = filters.isAuction === 'true'
    if (filters.specialCategory) params.specialCategory = filters.specialCategory
    if (filters.characteristics.length > 0) params.characteristics = filters.characteristics
    fetchVehicles(params)
  }

  const clearFilters = () => setFilters(EMPTY_FILTERS)

  const getActiveFiltersCount = () => {
    let count = 0
    Object.entries(filters).forEach(([key, value]) => {
      if (key === 'search') return
      if (Array.isArray(value) && value.length > 0) count++
      else if (value && value !== '') count++
    })
    return count
  }

  const getVehiclePrice = (vehicle: Vehicle): number =>
    typeof vehicle.salePrice === 'string' ? parseFloat(vehicle.salePrice) : (vehicle.salePrice || 0)

  const nextPhoto = (vehicleId: string, totalPhotos: number) =>
    setPhotoIndexes((prev) => ({
      ...prev,
      [vehicleId]: ((prev[vehicleId] || 0) + 1) % totalPhotos,
    }))

  const prevPhoto = (vehicleId: string, totalPhotos: number) =>
    setPhotoIndexes((prev) => ({
      ...prev,
      [vehicleId]: ((prev[vehicleId] || 0) - 1 + totalPhotos) % totalPhotos,
    }))

  const getCurrentPhotoIndex = (vehicleId: string) => photoIndexes[vehicleId] || 0

  const handleDeleteClick = (vehicle: Vehicle) => {
    setVehicleToDelete(vehicle)
    setDeleteDialogOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (!vehicleToDelete) return
    try {
      await deleteVehicle(vehicleToDelete.id)
      setDeleteDialogOpen(false)
      setVehicleToDelete(null)
      setSuccessMessage(t('vehicles.success.deleted'))
      setTimeout(() => setSuccessMessage(''), 5000)
    } catch (error) {
      console.error('Erro ao excluir veículo:', error)
    }
  }

  const formatPrice = (price: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(price)

  const StatusBadge = ({ status }: { status: string }) => {
    const cfg = STATUS_CONFIG[status] || { labelKey: status, variant: 'default' as const }
    return (
      <Badge variant={cfg.variant}>
        {t(cfg.labelKey)}
      </Badge>
    )
  }

  const activeCount = getActiveFiltersCount()

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-display">{t('vehicles.title')}</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {t('vehicles.subtitle')}
          </p>
        </div>
        <Button onClick={() => navigate('/vehicles/new')}>
          <Plus className="h-4 w-4 mr-2" />
          {t('vehicles.actions.addVehicle')}
        </Button>
      </div>

      {/* Feedback banners */}
      {successMessage && (
        <div className="flex items-center gap-3 bg-emerald-50 dark:bg-emerald-950 border border-emerald-200 dark:border-emerald-800 rounded-lg px-4 py-3">
          <CheckCircle className="h-4 w-4 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
          <p className="text-sm text-emerald-800 dark:text-emerald-200">{successMessage}</p>
        </div>
      )}
      {error && (
        <div className="flex items-center gap-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg px-4 py-3">
          <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400 flex-shrink-0" />
          <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatsCard
          title={t('vehicles.stats.total')}
          value={isLoading ? '—' : total}
          icon={Car}
          iconColor="text-accent"
          iconBg="bg-accent/10"
        />
        <StatsCard
          title={t('vehicles.stats.inStock')}
          value={isLoading ? '—' : vehicles.filter((v) => v.status === 'in_stock').length}
          icon={CheckCircle}
          iconColor="text-emerald-600"
          iconBg="bg-emerald-500/10"
        />
        <StatsCard
          title={t('vehicles.stats.totalValue')}
          value={
            isLoading
              ? '—'
              : formatPrice(vehicles.reduce((sum, v) => sum + getVehiclePrice(v), 0))
          }
          icon={DollarSign}
          iconColor="text-violet-600"
          iconBg="bg-violet-500/10"
        />
      </div>

      {/* Main layout: sidebar + content */}
      <div className="flex gap-6 items-start">
        {/* ── Left sidebar (desktop) ── */}
        <aside className="hidden lg:block w-60 xl:w-64 flex-shrink-0 sticky top-6">
          <VehicleFiltersComponent
            filters={filters}
            onFilterChange={setFilters}
            onClearFilters={clearFilters}
            activeCount={activeCount}
            brands={brands}
            models={models}
            versions={versions}
            options={options}
          />
        </aside>

        {/* ── Main content ── */}
        <div className="flex-1 min-w-0 space-y-4">
          {/* Search + toolbar */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                placeholder={t('vehicles.search.placeholder')}
                value={filters.search}
                onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
                className="pl-9"
              />
            </div>

            <div className="flex items-center gap-2">
              {/* Mobile filter toggle */}
              <Button
                variant="outline"
                onClick={() => setMobileFiltersOpen(!mobileFiltersOpen)}
                className="lg:hidden relative"
              >
                <Filter className="h-4 w-4 mr-2" />
                {t('vehicles.filters.title')}
                {activeCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 h-4 w-4 flex items-center justify-center text-[10px] font-bold rounded-full bg-accent text-white">
                    {activeCount}
                  </span>
                )}
              </Button>

              {activeCount > 0 && (
                <Button variant="outline" onClick={clearFilters} className="hidden lg:flex">
                  <X className="h-4 w-4 mr-1.5" />
                  {t('vehicles.filters.clear')}
                </Button>
              )}

              {/* View toggle */}
              <div className="flex border border-border rounded-md overflow-hidden">
                <button
                  onClick={() => setViewMode('grid')}
                  className={cn(
                    'px-3 py-2 transition-colors',
                    viewMode === 'grid'
                      ? 'bg-accent text-white'
                      : 'bg-card text-muted-foreground hover:bg-surface',
                  )}
                  title={t('vehicles.view.grid')}
                >
                  <Grid className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={cn(
                    'px-3 py-2 border-l border-border transition-colors',
                    viewMode === 'list'
                      ? 'bg-accent text-white'
                      : 'bg-card text-muted-foreground hover:bg-surface',
                  )}
                  title={t('vehicles.view.list')}
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Mobile filters panel */}
          {mobileFiltersOpen && (
            <div className="lg:hidden">
              <VehicleFiltersComponent
                filters={filters}
                onFilterChange={setFilters}
                onClearFilters={clearFilters}
                activeCount={activeCount}
                brands={brands}
                models={models}
                versions={versions}
                options={options}
              />
            </div>
          )}

          {/* Result count */}
          {!isLoading && vehicles.length > 0 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                <span className="font-semibold text-foreground tabular-nums">{vehicles.length}</span>{' '}
                {vehicles.length === 1 ? t('vehicles.results.one') : t('vehicles.results.multiple')}
                {activeCount > 0 && t('vehicles.results.withFilters')}
              </p>
            </div>
          )}

          {/* ── List view ── */}
          {viewMode === 'list' ? (
            <Card>
              <CardContent className="p-0">
                {isLoading ? (
                  <ListPageSkeleton variant="table" rows={6} showFilters={false} />
                ) : vehicles.length === 0 ? (
                  <EmptyState hasFilters={activeCount > 0} onAdd={() => navigate('/vehicles/new')} t={t} />
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('vehicles.table.vehicle')}</TableHead>
                        <TableHead>{t('vehicles.table.year')}</TableHead>
                        <TableHead>{t('vehicles.table.price')}</TableHead>
                        <TableHead>{t('vehicles.table.status')}</TableHead>
                        <TableHead>{t('vehicles.table.km')}</TableHead>
                        <TableHead className="text-right">{t('vehicles.table.actions')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {vehicles.map((vehicle) => (
                        <TableRow key={vehicle.id} hover>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="h-11 w-16 rounded-md overflow-hidden border border-border flex-shrink-0 bg-surface">
                                {vehicle.photos && vehicle.photos.length > 0 ? (
                                  <img
                                    src={vehicle.photos[0].url}
                                    alt={vehicle.identification?.plate || 'Veículo'}
                                    className="h-full w-full object-cover"
                                    onError={(e) => {
                                      e.currentTarget.style.display = 'none'
                                    }}
                                  />
                                ) : (
                                  <div className="h-full w-full flex items-center justify-center">
                                    <Car className="h-5 w-5 text-muted-foreground/40" />
                                  </div>
                                )}
                              </div>
                              <div>
                                <p className="font-semibold text-foreground text-sm">
                                  {vehicle.name ||
                                    vehicle.identification?.plate ||
                                    vehicle.identification?.chassis ||
                                    `ID: ${vehicle.id.substring(0, 8)}`}
                                </p>
                                <p className="text-xs text-muted-foreground capitalize">
                                  {[vehicle.specifications?.body?.color, vehicle.specifications?.engine?.type, vehicle.specifications?.engine?.transmission]
                                    .filter(Boolean)
                                    .join(' · ')}
                                </p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm tabular-nums">
                            {vehicle.manufactureYear}/{vehicle.modelYear}
                          </TableCell>
                          <TableCell>
                            <span
                              className="text-sm font-semibold tabular-nums"
                              style={{ fontFamily: '"JetBrains Mono", monospace' }}
                            >
                              {formatPrice(getVehiclePrice(vehicle))}
                            </span>
                          </TableCell>
                          <TableCell>
                            <StatusBadge status={vehicle.status} />
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground tabular-nums">
                            {vehicle.mileage?.toLocaleString('pt-BR') || '0'} km
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center justify-end gap-1">
                              <button
                                onClick={() => navigate(`/vehicles/${vehicle.id}`)}
                                className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-surface transition-colors"
title={t('vehicles.actions.view')}
                                >
                                  <Eye className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => navigate(`/vehicles/edit/${vehicle.id}`)}
                                  className="p-1.5 rounded-md text-muted-foreground hover:text-accent hover:bg-accent/10 transition-colors"
                                  title={t('vehicles.actions.edit')}
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteClick(vehicle)}
                                  className="p-1.5 rounded-md text-muted-foreground hover:text-red-600 hover:bg-red-50 transition-colors"
                                  title={t('vehicles.actions.delete')}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          ) : (
            /* ── Grid view ── */
            <div>
              {isLoading ? (
                <ListPageSkeleton variant="cards" rows={6} showFilters={false} />
              ) : vehicles.length === 0 ? (
                <EmptyState hasFilters={activeCount > 0} onAdd={() => navigate('/vehicles/new')} t={t} />
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
                  {vehicles.map((vehicle) => {
                    const photoIndex = getCurrentPhotoIndex(vehicle.id)
                    const hasPhotos = vehicle.photos && vehicle.photos.length > 0
                    const photoCount = vehicle.photos?.length || 0

                    return (
                      <div
                        key={vehicle.id}
                        className="group bg-card border border-border rounded-lg overflow-hidden shadow-precision hover:shadow-md hover:border-accent/20 transition-all duration-200"
                      >
                        {/* Photo area */}
                        <div className="relative aspect-[4/3] bg-surface overflow-hidden">
                          {hasPhotos ? (
                            <img
                              src={vehicle.photos![photoIndex].url}
                              alt={vehicle.name || 'Veículo'}
                              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.03] cursor-pointer"
                              onClick={() => navigate(`/vehicles/${vehicle.id}`)}
                            />
                          ) : (
                            <div
                              className="w-full h-full flex items-center justify-center cursor-pointer"
                              onClick={() => navigate(`/vehicles/${vehicle.id}`)}
                            >
                              <Car className="h-14 w-14 text-border" />
                            </div>
                          )}

                          {/* Status badge — top left */}
                          <div className="absolute top-2.5 left-2.5">
                            <StatusBadge status={vehicle.status} />
                          </div>

                          {/* Actions — top right, revealed on hover */}
                          <div className="absolute top-2.5 right-2.5 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                navigate(`/vehicles/${vehicle.id}`)
                              }}
                              className="p-1.5 bg-card/90 backdrop-blur-sm border border-border/50 rounded-md hover:bg-card text-foreground transition-colors"
title={t('vehicles.actions.view')}
                              >
                                <Eye className="h-3.5 w-3.5" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  navigate(`/vehicles/edit/${vehicle.id}`)
                                }}
                                className="p-1.5 bg-card/90 backdrop-blur-sm border border-border/50 rounded-md hover:bg-card text-foreground transition-colors"
                                title={t('vehicles.actions.edit')}
                              >
                                <Edit className="h-3.5 w-3.5" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleDeleteClick(vehicle)
                                }}
                                className="p-1.5 bg-card/90 backdrop-blur-sm border border-border/50 rounded-md hover:bg-red-50 hover:text-red-600 text-foreground transition-colors"
                                title={t('vehicles.actions.delete')}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>

                          {/* Photo navigation */}
                          {photoCount > 1 && (
                            <>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  prevPhoto(vehicle.id, photoCount)
                                }}
                                className="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 bg-card/80 backdrop-blur-sm rounded-full opacity-0 group-hover:opacity-100 transition-opacity border border-border/30 hover:bg-card"
                              >
                                <ChevronLeft className="h-3.5 w-3.5 text-foreground" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  nextPhoto(vehicle.id, photoCount)
                                }}
                                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 bg-card/80 backdrop-blur-sm rounded-full opacity-0 group-hover:opacity-100 transition-opacity border border-border/30 hover:bg-card"
                              >
                                <ChevronRight className="h-3.5 w-3.5 text-foreground" />
                              </button>

                              {/* Dot indicators */}
                              <div className="absolute bottom-2.5 left-1/2 -translate-x-1/2 flex gap-1">
                                {vehicle.photos!.slice(0, 6).map((_, index) => (
                                  <button
                                    key={index}
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      setPhotoIndexes((prev) => ({ ...prev, [vehicle.id]: index }))
                                    }}
                                    className={cn(
                                      'h-1 rounded-full transition-all duration-200',
                                      index === photoIndex
                                        ? 'w-4 bg-white'
                                        : 'w-1 bg-white/50 hover:bg-white/80',
                                    )}
                                  />
                                ))}
                              </div>
                            </>
                          )}

                          {/* Photo count chip */}
                          {photoCount > 1 && (
                            <div className="absolute bottom-2.5 right-2.5 flex items-center gap-1 bg-muted text-muted-foreground text-[10px] font-medium px-1.5 py-0.5 rounded">
                              <Camera className="h-2.5 w-2.5" />
                              {photoCount}
                            </div>
                          )}
                        </div>

                        {/* Card body */}
                        <div
                          className="p-4 cursor-pointer"
                          onClick={() => navigate(`/vehicles/${vehicle.id}`)}
                        >
                          {/* Name */}
                          <h3 className="font-semibold text-foreground line-clamp-1 mb-2 group-hover:text-accent transition-colors">
                            {vehicle.name ||
                              vehicle.identification?.plate ||
                              vehicle.identification?.chassis ||
                              t('vehicles.noIdentification')}
                          </h3>

                          {/* Key specs row */}
                          <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {vehicle.modelYear}
                            </span>
                            {vehicle.mileage !== undefined && (
                              <>
                                <span className="text-border">·</span>
                                <span className="flex items-center gap-1">
                                  <Gauge className="h-3 w-3" />
                                  {vehicle.mileage.toLocaleString('pt-BR')} km
                                </span>
                              </>
                            )}
                            {vehicle.specifications?.engine?.type && (
                              <>
                                <span className="text-border">·</span>
                                <span className="capitalize">{vehicle.specifications?.engine?.type}</span>
                              </>
                            )}
                          </div>

                          {/* Price */}
                          <div className="border-t border-border pt-3 mt-1">
                            <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-0.5">
                              {t('vehicles.priceLabel')}
                            </p>
                            <p
                              className="text-xl font-semibold text-foreground tabular-nums"
                              style={{ fontFamily: '"JetBrains Mono", monospace' }}
                            >
                              {formatPrice(getVehiclePrice(vehicle))}
                            </p>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        isOpen={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false)
          setVehicleToDelete(null)
        }}
        onConfirm={handleConfirmDelete}
        title={t('vehicles.dialog.deleteTitle')}
        description={t('vehicles.dialog.deleteDescription', {
          name: vehicleToDelete?.name || vehicleToDelete?.identification?.plate || vehicleToDelete?.identification?.chassis || t('vehicles.thisVehicle'),
        })}
        confirmText={t('vehicles.dialog.confirm')}
        cancelText={t('vehicles.dialog.cancel')}
        type="danger"
      />
    </div>
  )
}

const EmptyState = ({
  hasFilters,
  onAdd,
  t,
}: {
  hasFilters: boolean
  onAdd: () => void
  t: (key: string) => string
}) => (
  <div className="text-center py-16 px-4">
    <div className="mx-auto h-12 w-12 rounded-full bg-surface flex items-center justify-center mb-4">
      <Car className="h-6 w-6 text-muted-foreground/50" />
    </div>
    <p className="text-sm font-medium text-foreground mb-1">
      {hasFilters ? t('vehicles.empty.filters') : t('vehicles.empty.noVehicles')}
    </p>
    <p className="text-xs text-muted-foreground mb-4">
      {hasFilters
        ? t('vehicles.empty.adjustFilters')
        : t('vehicles.empty.addFirst')}
    </p>
    {!hasFilters && (
      <Button size="sm" onClick={onAdd}>
        <Plus className="h-4 w-4 mr-2" />
        {t('vehicles.actions.register')}
      </Button>
    )}
  </div>
)
